import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import styled from 'styled-components'
import axiosClient from '../api/axiosClient'
import { useAuth } from '../context/AuthContext'
import Modal from './Modal/Modal'
import { theme } from '../theme'
import Typography from './Typography'
import Tag from './Tag'
import Button from './Button/Button'
import ChevronDownIcon from '../components/Icons/ChevronDownIcon'
import DeleteOutlineIcon from '../components/Icons/DeleteOutlineIcon'

const StripeAccountReactivation = ({ onAccountReactivated, onCancel }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [availableAccounts, setAvailableAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [reactivating, setReactivating] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [expandedAccounts, setExpandedAccounts] = useState(new Set())
  const [creatingNewAccount, setCreatingNewAccount] = useState(false)

  useEffect(() => {
    fetchAvailableAccounts()
  }, [])

  const fetchAvailableAccounts = async () => {
    try {
      setLoading(true)
      const response = await axiosClient.get('/api/stripe/available-accounts')

      if (response.data.success) {
        setAvailableAccounts(response.data.accounts)
      } else {
        toast.error(response.data.message || 'Error al obtener cuentas disponibles')
      }
    } catch (error) {
      console.error('Error fetching available accounts:', error)
      toast.error('Error al obtener cuentas disponibles')
    } finally {
      setLoading(false)
    }
  }

  const handleReactivateAccount = async (accountId) => {
    try {
      setReactivating(accountId)
      const response = await axiosClient.post('/api/stripe/reactivate-account', {
        accountId
      })

      if (response.data.success) {
        toast.success(response.data.message || 'Cuenta reactivada exitosamente')

        // Redirigir al onboarding si es necesario
        if (response.data.onboardingUrl) {
          window.location.href = response.data.onboardingUrl
        } else {
          onAccountReactivated && onAccountReactivated(response.data)
        }
      } else {
        toast.error(response.data.message || 'Error al reactivar la cuenta')
      }
    } catch (error) {
      console.error('Error reactivating account:', error)
      toast.error('Error al reactivar la cuenta')
    } finally {
      setReactivating(null)
    }
  }

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta cuenta del historial? Esta acción no se puede deshacer.')) {
      return
    }

    try {
      setDeleting(accountId)
      const response = await axiosClient.delete(`/api/stripe/delete-historical-account/${accountId}`)

      if (response.data.success) {
        toast.success('Cuenta eliminada del historial exitosamente')
        // Actualizar la lista de cuentas disponibles
        setAvailableAccounts(prev => prev.filter(account => account.accountId !== accountId))
      } else {
        toast.error(response.data.message || 'Error al eliminar la cuenta')
      }
    } catch (error) {
      console.error('Error deleting account:', error)
      toast.error('Error al eliminar la cuenta del historial')
    } finally {
      setDeleting(null)
    }
  }

  const toggleAccountExpansion = (accountId) => {
    const newExpanded = new Set(expandedAccounts)
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId)
    } else {
      newExpanded.add(accountId)
    }
    setExpandedAccounts(newExpanded)
  }

  const handleCreateNewAccount = async () => {
    try {
      setCreatingNewAccount(true)
      // Obtener el userId del contexto de autenticación
      if (!user?.id) {
        throw new Error('Usuario no encontrado')
      }

      const response = await axiosClient.post('/api/stripe/create-or-connect-account', {
        userId: user.id,
        forceNew: true
      })

      if (response.data.onboardingUrl) {
        // Abrir en nueva ventana para evitar que Stripe use información de sesión previa
        // que puede pre-llenar y deshabilitar el campo de email
        window.open(response.data.onboardingUrl, '_blank')
      } else {
        toast.success('Cuenta de Stripe creada exitosamente')
        onAccountReactivated && onAccountReactivated(response.data)
      }
    } catch (error) {
      console.error('Error creating new account:', error)
      toast.error('Error al crear nueva cuenta de Stripe')
    } finally {
      setCreatingNewAccount(false)
    }
  }

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: es })
    } catch {
      return dateString
    }
  }

  const getStatusBadge = (account) => {
    const { currentStatus } = account

    if (currentStatus.chargesEnabled && currentStatus.payoutsEnabled) {
      return <Tag $variant="success">Habilitada</Tag>
    } else if (currentStatus.detailsSubmitted) {
      return <Tag $variant="warning">Pendiente de verificación</Tag>
    } else {
      return <Tag $variant="warning">Pendiente de verificación</Tag>
    }
  }

  if (loading) {
    return (
      <LoadingContainer>
        <div className="spinner" role="status" aria-label="Cargando"></div>
        <p>Buscando cuentas disponibles...</p>
      </LoadingContainer>
    )
  }

  if (availableAccounts.length === 0) {
    return (
      <Modal
        title="Conectar cuenta de Stripe"
        onClose={onCancel}
        maxWidth="500px"
      >
        <EmptyState>
          <Typography as="h5" $variant="body-1-medium" color={theme.colors.defaultStrong}>No hay cuentas conectadas previamente</Typography>
          <Typography as="p" $variant="body-2-regular" color={theme.colors.defaultSubtle}>No tienes cuentas de Stripe registradas anteriormente. Puedes crear una nueva cuenta.</Typography>
          <Button
            onClick={handleCreateNewAccount}
            disabled={creatingNewAccount}
            style={{ marginTop: theme.sizing.md }}
          >
            {creatingNewAccount ? 'Creando cuenta...' : 'Crear nueva cuenta de Stripe'}
          </Button>
        </EmptyState>
      </Modal>
    )
  }

  return (
    <Modal
      title="Conectar cuenta de Stripe"
      onClose={onCancel}
      maxWidth="800px"
      maxHeight="80vh"
    >
      <Container>
        <InfoAlert>
          <strong>💡 Tip:</strong> Puedes reactivar una cuenta previamente desconectada en lugar de crear una nueva.
          Esto mantiene tu historial de transacciones y configuración.
        </InfoAlert>

        <AccountsContainer>
          {availableAccounts.map((account) => {
            const isReactivating = reactivating === account.accountId
            const isDeleting = deleting === account.accountId
            const isExpanded = expandedAccounts.has(account.accountId)

            return (
              <AccountCard key={account.accountId}>
                <AccountHeader>
                  <ToggleButton
                    $variant="ghost"
                    $iconOnly
                    onClick={() => toggleAccountExpansion(account.accountId)}
                    className={isExpanded ? 'expanded' : ''}
                    title={isExpanded ? 'Colapsar detalles' : 'Expandir detalles'}
                  >
                    {/* <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 10l5 5 5-5z" />
                      </svg> */}
                    <ChevronDownIcon height={20} width={20} fill={theme.colors.defaultWeak} />
                  </ToggleButton>
                  <AccountInfo>
                    <InfoCombo>
                      <Typography as="p" $variant="body-1-medium">
                        {account.email || 'Email no disponible'}
                      </Typography>
                      <Typography as="p" $variant="body-1-regular" color={theme.colors.defaultSubtle}>
                        ·
                      </Typography>
                      <Typography as="p" $variant="body-1-regular" color={theme.colors.defaultSubtle}>
                        {account.businessProfile?.url || 'URL no disponible'}
                      </Typography>
                    </InfoCombo>
                    {getStatusBadge(account)}
                  </AccountInfo>
                  <ActionsContainer>
                    <Button
                      $variant="outline"
                      onClick={() => handleReactivateAccount(account.accountId)}
                      disabled={isReactivating || isDeleting}
                      className="primary"
                    >
                      {isReactivating ? 'Reactivando...' : 'Reactivar'}
                    </Button>
                    <Button
                      $variant="outlineDanger"
                      $iconOnly
                      onClick={() => handleDeleteAccount(account.accountId)}
                      disabled={isReactivating || isDeleting}
                      className="danger"
                    >
                      {/* {isDeleting ? 'Eliminando...' : 'Eliminar'} */}
                      <DeleteOutlineIcon height={24} width={24} fill={theme.colors.errorMain} />
                    </Button>
                  </ActionsContainer>
                </AccountHeader>

                {/* Sección expandible con detalles adicionales */}
                {isExpanded && (
                  <ExpandedContent>
                    <ContentColumn>
                      <DetailItem>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>
                          Nombre del negocio:
                        </Typography>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                          {account.businessProfile?.name || account.company?.name || (account.individual?.firstName || account.individual?.lastName ? `${account.individual?.firstName || ''} ${account.individual?.lastName || ''}`.trim() : null) || account.email || 'No configurado'}
                        </Typography>
                      </DetailItem>
                      {(account.individual?.firstName || account.individual?.lastName) && (
                        <DetailItem>
                          <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>Nombre personal:</Typography>
                          <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                            {`${account.individual?.firstName || ''} ${account.individual?.lastName || ''}`.trim()}
                          </Typography>
                        </DetailItem>
                      )}
                      <DetailItem>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>Tipo de negocio:</Typography>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                          {account.businessType || 'No especificado'}
                        </Typography>
                      </DetailItem>
                      <DetailItem>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>País:</Typography>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                          {account.country || 'No especificado'}
                        </Typography>
                      </DetailItem>
                      <DetailItem>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>Creada:</Typography>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                          {account.created ? formatDate(new Date(account.created * 1000)) : 'No disponible'}
                        </Typography>
                      </DetailItem>

                      <DetailItem>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>ID de cuenta:</Typography>
                        <Tag $variant="subtle">{account.accountId}</Tag>
                      </DetailItem>
                    </ContentColumn>
                    <ContentColumn>


                      <DetailItem>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>Desconectada:</Typography>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                          {formatDate(account.disconnectedAt)}
                        </Typography>

                      </DetailItem>
                      {account.reason && (
                        <DetailItem>
                          <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>Motivo:</Typography>
                          <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                            {account.reason}
                          </Typography>
                        </DetailItem>
                      )}
                      <DetailItemColumn>
                        <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>Estado de la cuenta:</Typography>
                        <StatusList>
                          <DetailItem>
                            <Typography $variant="body-3-medium" color={theme.colors.defaultWeak} style={{ width: "52px" }}>Pagos:</Typography>
                            <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                              {account.currentStatus.chargesEnabled ? '✅ Habilitados' : '❌ Deshabilitados'}
                            </Typography>
                          </DetailItem>
                          <DetailItem>
                            <Typography $variant="body-3-medium" color={theme.colors.defaultWeak} style={{ width: "52px" }}>Retiros:</Typography>
                            <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                              {account.currentStatus.payoutsEnabled ? '✅ Habilitados' : '❌ Deshabilitados'}                            </Typography>
                          </DetailItem>
                          <DetailItem>
                            <Typography $variant="body-3-medium" color={theme.colors.defaultWeak} style={{ width: "52px" }}>Detalles:</Typography>
                            <Typography $variant="body-3-medium" color={theme.colors.defaultStrong}>
                              {account.currentStatus.detailsSubmitted ? '✅ Enviados' : '❌ Pendientes'}                            </Typography>
                          </DetailItem>
                        </StatusList>
                      </DetailItemColumn>
                      {account.verification && (account.verification.currently_due?.length > 0 || account.verification.past_due?.length > 0) && (
                        <DetailItem>
                          <Typography $variant="body-3-medium" color={theme.colors.defaultWeak}>Verificación pendiente:</Typography>
                          <StatusList>
                            {account.verification.currently_due?.map((item, index) => (
                              <li key={index}>• {item}</li>
                            ))}
                            {account.verification.past_due?.map((item, index) => (
                              <li key={index} style={{ color: '#e74c3c' }}>• {item} (vencido)</li>
                            ))}
                          </StatusList>
                        </DetailItem>
                      )}
                    </ContentColumn>
                  </ExpandedContent>
                )}
              </AccountCard>
            )
          })}

          <FooterNote>
            <Typography as="p" $variant="body-2-regular" color={theme.colors.defaultSubtle}>
              ¿No encuentras la cuenta que buscas?
            </Typography>
            <Button
              $variant="outline"
              onClick={handleCreateNewAccount}
              disabled={creatingNewAccount}
              style={{ marginTop: theme.sizing.sm, alignSelf: 'flex-start' }}
            >
              {creatingNewAccount ? 'Creando cuenta...' : 'Crear nueva cuenta de Stripe'}
            </Button>
          </FooterNote>
        </AccountsContainer>
      </Container>
    </Modal>
  )
}

export default StripeAccountReactivation




// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${theme.sizing.sm};
  gap: ${theme.sizing.sm};
`

const InfoAlert = styled.div`
  background-color: ${theme.fill.defaultSubtle};
  // background-color: ${theme.fill.successAlphaMain16};
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.xs};
  padding: ${theme.sizing.sm};
  // margin-bottom: ${theme.sizing.lg};
  color: ${theme.colors.defaultMain};
  
  strong {
    color: ${theme.colors.successMain};
  }
`

const AccountsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.sizing.md};
`

const AccountCard = styled.div`
  border: 1px solid ${theme.border.defaultWeak};
  border-radius: ${theme.radius.xs};
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px ${theme.fill.defaultAlphaMain16};
  }
`

const AccountHeader = styled.div`
  padding: ${theme.sizing.sm};
  background-color: ${theme.fill.defaultMain};
  gap: ${theme.sizing.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AccountInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.sizing.xs};
`;
const InfoCombo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.sizing.xs};
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.sizing.xs};
  margin-left: ${theme.sizing.sm};
`;

const ToggleButton = styled(Button)`
  &.expanded svg {
    transform: rotate(180deg);
  }
`;

const ExpandedContent = styled.div`
  padding: ${theme.sizing.sm};
  background-color: ${theme.fill.defaultSubtle};
  border-top: 1px solid ${theme.border.defaultWeak};
  display: flex;
  flex-direction: row;
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.sizing.xs};
  flex-grow: 1;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.sizing.xs};
`

const DetailItemColumn = styled(DetailItem)`
  flex-direction: column;
  align-items: flex-start;
`

const StatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.sizing.xs};
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.sizing.lg};
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid ${theme.fill.defaultWeak};
    border-top: 4px solid ${theme.fill.brandMain};
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto ${theme.sizing.sm};
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  p {
    color: ${theme.colors.defaultWeak};
    margin: 0;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.sizing.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.sizing.xs};
  flex-grow: 1;
  width: 100%;
`

const FooterNote = styled.div`
  // margin-top: ${theme.sizing.lg};
  display: flex;
  flex-direction: column;
  padding-top: ${theme.sizing.sm};
  padding-bottom: ${theme.sizing.sm};
`
